import PropTypes from 'prop-types';
import { Box, Typography, IconButton } from '@mui/material';
import { Iconify } from 'src/components';
import {
  headerSx,
  headerLeftSx,
  headerIconSx,
  headerTitleSx,
  headerBadgeSx,
  headerRightSx,
  progressMiniSx,
  saveIndicatorSx,
} from '../styles';

const CallScriptHeader = ({
  title,
  subtitle,
  type = 'inbound',
  scriptCategory,
  currentStep = 1,
  totalSteps = 7,
  saveStatus = 'saved',
  onClose,
  onToggleContext,
  showContextToggle = false,
  draftSlot = null,
}) => {
  const progressPercent = Math.round((currentStep / totalSteps) * 100);
  const circumference = 2 * Math.PI * 13;
  const dashOffset = circumference - (progressPercent / 100) * circumference;

  const isTCO = scriptCategory === 'TCO';
  const getTypeIcon = () => {
    if (isTCO) return 'mdi:clipboard-text-outline';
    if (type === 'inbound') return 'mdi:call-received';
    return 'mdi:call-made';
  };
  const typeIcon = getTypeIcon();
  const getSaveIcon = () => {
    if (saveStatus === 'saving') return 'mdi:sync';
    if (saveStatus === 'waiting_for_lead') return 'mdi:account-alert';
    return 'mdi:cloud-check';
  };
  const getSaveLabel = () => {
    if (saveStatus === 'saving') return 'Saving...';
    if (saveStatus === 'waiting_for_lead') return 'Waiting for caller ID...';
    return 'Saved';
  };
  const saveIcon = getSaveIcon();

  return (
    <Box component="header" sx={headerSx}>
      <Box sx={headerLeftSx}>
        <Box sx={headerIconSx(type)}>
          <Iconify icon="mdi:file-document-outline" width={24} height={24} />
        </Box>
        <Box sx={headerTitleSx}>
          <Typography component="h1">{title}</Typography>
          <Typography component="p">{subtitle}</Typography>
        </Box>
        <Box sx={headerBadgeSx(type)}>
          <Iconify icon={typeIcon} width={16} height={16} />
          {(() => { if (isTCO) return 'TCO'; if (type === 'inbound') return 'Inbound'; return 'Outbound'; })()}
        </Box>
      </Box>

      <Box sx={headerRightSx}>
        <Box sx={saveIndicatorSx(saveStatus)}>
          <Iconify
            icon={saveIcon}
            width={14}
            height={14}
            sx={saveStatus === 'saving' ? { animation: 'spin 1s linear infinite' } : {}}
          />
          <span>{getSaveLabel()}</span>
        </Box>

        {draftSlot}

        <Box sx={progressMiniSx}>
          <Box sx={{ position: 'relative', width: 32, height: 32 }}>
            <svg width="32" height="32" style={{ transform: 'rotate(-90deg)' }}>
              <circle
                cx="16"
                cy="16"
                r="13"
                fill="none"
                stroke="#E0E0E0"
                strokeWidth="3"
              />
              <circle
                cx="16"
                cy="16"
                r="13"
                fill="none"
                stroke="#00A76F"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                style={{ transition: 'stroke-dashoffset 0.4s ease' }}
              />
            </svg>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                fontWeight: 700,
                color: '#007867',
              }}
            >
              {progressPercent}%
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            <strong style={{ color: '#212B36' }}>{currentStep}</strong> of {totalSteps}
          </Typography>
        </Box>

        {showContextToggle && (
          <IconButton
            onClick={onToggleContext}
            sx={{
              display: { xs: 'flex', md: 'none' },
              bgcolor: '#C8FAD6',
              color: '#007867',
              borderRadius: 1,
              '&:hover': { bgcolor: '#5BE49B' },
            }}
          >
            <Iconify icon="mdi:account" width={24} height={24} />
          </IconButton>
        )}

        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onClose?.(e);
          }}
          sx={{ color: 'text.secondary' }}
        >
          <Iconify icon="mdi:close" width={24} height={24} />
        </IconButton>
      </Box>
    </Box>
  );
};

CallScriptHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  type: PropTypes.oneOf(['inbound', 'outbound']),
  scriptCategory: PropTypes.string,
  currentStep: PropTypes.number,
  totalSteps: PropTypes.number,
  saveStatus: PropTypes.oneOf(['saved', 'saving', 'idle', 'waiting_for_lead']),
  onClose: PropTypes.func,
  draftSlot: PropTypes.node,
  onToggleContext: PropTypes.func,
  showContextToggle: PropTypes.bool,
};

export default CallScriptHeader;
